import { useState, useMemo, useEffect } from "react";
import generalApi from "../../../api/general";
import { useParams } from "react-router";

function ReadAndAsnwerRegex({ activityid, questiondata, onLoadExisting, onSubmit }) {
  const defaultText = questiondata.defaultText || "Enter text here";
  const defaultRegex = questiondata.defaultRegex || "\\w+";
  const readOnly = questiondata.readOnly || false;

  const [isGraded, setIsGraded] = useState(!!questiondata.isGraded);
  const [text, setText] = useState(defaultText);
  const [regexInput, setRegexInput] = useState(isGraded ? "" : defaultRegex);
  const [ignoreCase, setIgnoreCase] = useState(true);
  const [error, setError] = useState(null);
  const [answerAlreadyCorrect, setAnswerAlreadyCorrect] = useState(false);
  const params = useParams();
  const [gradeResult, setGradeResult] = useState(null);

  useEffect(() => {
    if (isGraded) setText(defaultText);
  }, [isGraded, defaultText]);

  const textAreaDisabled = readOnly || isGraded;

  const updateAnswers = () => {
    const activityAnswers = generalApi
      .getActivityAnswer(activityid, params.courseid)
      .then((activityAnswers) => {
        console.log(activityAnswers.data.results.answer.userAnswerString);
        console.log(activityAnswers.data.results.answer.success);
        if (activityAnswers && activityAnswers?.data?.results?.answer?.userAnswerString) {
          setRegexInput(activityAnswers.data.results.answer.userAnswerString);
        }
        if (activityAnswers && activityAnswers?.data?.results?.answer?.success) {
          setAnswerAlreadyCorrect(true);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  };

  useEffect(() => {
    if (isGraded) {
      updateAnswers();
    }
  }, []);

  // ---------- helpers ----------
  const safeBuildRegex = (pattern, flags) => {
    try {
      return { regex: new RegExp(pattern, flags), error: null };
    } catch (e) {
      return { regex: null, error: "Invalid regular expression" };
    }
  };

  const collectMatches = (sourceText, regex) => {
    const startTime = performance.now();
    const results = [];
    if (!regex) return results;

    // Ensure global for iteration
    const r = new RegExp(regex.source, regex.flags.includes("g") ? regex.flags : regex.flags + "g");
    let m;
    let guard = 0;
    while ((m = r.exec(sourceText)) !== null) {
      // catastrophic backtracking guard
      if (performance.now() - startTime > 100) {
        throw new Error("Regex took too long to evaluate");
      }
      const found = m[0];
      const start = m.index;
      const end = start + found.length;
      results.push({ start, end, text: found });

      // guard zero-length matches to avoid infinite loops
      if (found.length === 0) {
        r.lastIndex = r.lastIndex + 1;
      }

      // hard-guard (paranoia)
      if (++guard > 100000) {
        throw new Error("Regex took too long to evaluate");
      }
    }
    return results;
  };

  const toKeySet = (arr) => {
    const s = new Set();
    for (const it of arr) s.add(`${it.start}-${it.end}`);
    return s;
  };

  const diffMatches = (gt, user) => {
    const gtSet = toKeySet(gt);
    const userSet = toKeySet(user);

    const missing = gt.filter((m) => !userSet.has(`${m.start}-${m.end}`));
    const extra = user.filter((m) => !gtSet.has(`${m.start}-${m.end}`));

    return { missing, extra, counts: { gt: gt.length, user: user.length } };
  };

  const highlightedText = useMemo(() => {
    const start = performance.now();

    if (!regexInput) {
      const message = "Enter a regex to show the matching sequences.";
      return { parts: [{ text, match: false }], error: message };
    }

    try {
      const flags = ignoreCase ? "gi" : "g";
      const { regex, error: compileErr } = safeBuildRegex(regexInput, flags);
      if (compileErr) return { parts: [{ text, match: false }], error: compileErr };
      if (!regex) return { parts: [{ text, match: false }], error: "Invalid regular expression" };

      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(text)) !== null) {
        if (performance.now() - start > 100) {
          throw new Error("Regex took too long to evaluate");
        }
        const before = text.slice(lastIndex, match.index);
        const found = match[0];
        if (before) parts.push({ text: before, match: false });
        parts.push({ text: found, match: true });
        lastIndex = regex.lastIndex;

        // zero-length guard
        if (found.length === 0) regex.lastIndex = regex.lastIndex + 1;
      }

      if (lastIndex < text.length) parts.push({ text: text.slice(lastIndex), match: false });

      return { parts, error: null };
    } catch (err) {
      const message =
        err.message === "Regex took too long to evaluate"
          ? "⚠️ This pattern runs too slowly or causes backtracking."
          : "Invalid regular expression";
      return { parts: [{ text, match: false }], error: message };
    }
  }, [text, regexInput, ignoreCase]);

  // keep error state in sync, but outside of useMemo
  useEffect(() => {
    setError(highlightedText.error);
  }, [highlightedText.error]);

  const { parts, error: regexError } = highlightedText;

  const handleCheck = () => {
    if (!isGraded) return;

    const flags = ignoreCase ? "gi" : "g";

    // compile ground truth
    const gtCompile = safeBuildRegex(defaultRegex, flags);
    if (gtCompile.error) {
      setGradeResult({ missing: [], extra: [], counts: { gt: 0, user: 0 }, error: "Ground-truth regex is invalid." });
      return;
    }
    // compile user
    const userCompile = safeBuildRegex(regexInput, flags);
    if (userCompile.error) {
      setGradeResult({ missing: [], extra: [], counts: { gt: 0, user: 0 }, error: "Your regex is invalid." });
      return;
    }

    try {
      const gtMatches = collectMatches(defaultText, gtCompile.regex);
      const userMatches = collectMatches(defaultText, userCompile.regex);
      const diff = diffMatches(gtMatches, userMatches);
      let score = 0;

      if (diff.missing.length === 0 && diff.extra.length === 0) {
        score = 1;
      } else if (diff.missing.length < diff.counts.gt && diff.extra.length < diff.counts.gt) {
        score = 0.5;
      } else {
        score = 0;
      }
      setGradeResult({ ...diff, error: null });
      onSubmit(activityid, { input: regexInput, result: diff, score: score });
      if (score==1) {
        setAnswerAlreadyCorrect(true);
      }
    } catch (e) {
      const msg =
        e.message === "Regex took too long to evaluate"
          ? "⚠️ Your regex took too long to evaluate (possible catastrophic backtracking)."
          : "An error occurred while grading.";
      setGradeResult({ missing: [], extra: [], counts: { gt: 0, user: 0 }, error: msg });
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-4 min-w-full p-4 bg-gray-50 rounded-lg shadow-md">
      {/* LEFT SIDE */}
      <div className="flex-1 space-y-3">
        <div>
          {!isGraded && (
            <>
              <label className="block font-semibold text-gray-700 mb-1">Test String:</label>
              <textarea
                className={`w-full p-2 border rounded-md font-mono text-left whitespace-pre text-sm ${
                  textAreaDisabled ? "cursor-not-allowed bg-gray-100 text-gray-500" : "cursor-text"
                }`}
                rows="4"
                value={text}
                disabled={textAreaDisabled}
                onChange={(e) => setText(e.target.value)}
              />
            </>
          )}
        </div>

        <div>
          <label className="block font-semibold text-gray-700 mb-1">Regular Expression:</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              className={`flex-1 p-2 border rounded-md font-mono text-left whitespace-pre text-sm 
                ${readOnly ? "cursor-not-allowed bg-gray-100 text-gray-500" : "cursor-text"}
                ${answerAlreadyCorrect ? "cursor-not-allowed bg-green-100 text-white-500" : "cursor-text"}
              }`}
              value={regexInput}
              disabled={readOnly || (isGraded && answerAlreadyCorrect)}
              onChange={(e) => setRegexInput(e.target.value)}
            />
            {isGraded && (
              <button
                type="button"
                onClick={handleCheck}
                disabled={error || answerAlreadyCorrect}
                className={`px-3 py-2 rounded-md bg-accent text-white text-sm hover:bg-accentdark ${error ? "opacity-50" : ""}`}
              >
                Grade
              </button>
            )}
          </div>
        </div>

      </div>

      {/* RIGHT SIDE */}
      <div className="flex-1">
        <label className="block font-semibold text-gray-700 mb-1">Output:</label>
        <div
          className="p-2 border rounded-md bg-white font-mono text-sm leading-relaxed text-left whitespace-pre-wrap break-words"
          style={{ minHeight: "7rem" }}
        >
          {parts.map((part, i) =>
            part.match ? (
              <span key={i} className={`${i % 2 === 0 ? "bg-yellow-300 text-black" : "bg-cyan-300 text-black"} rounded-sm`}>
                {part.text}
              </span>
            ) : (
              <span key={i}>{part.text}</span>
            )
          )}
        </div>

        {(error || regexError) && <div className="text-red-600 text-sm mt-2">{error || regexError}</div>}

        {isGraded && gradeResult && (
          <div className="mt-4 p-3 border rounded-md bg-white">
            <div className="font-semibold mb-2">Result</div>
            {gradeResult.error ? (
              <div className="text-red-600 text-sm">{gradeResult.error}</div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <div className="font-semibold text-sm mb-1">Missing (should have matched):</div>
                    {gradeResult.missing.length === 0 ? (
                      <div className="text-xs text-green-700">None 🎉</div>
                    ) : (
                      <ul className="list-disc list-inside text-sm">
                        {gradeResult.missing.map((m, idx) => (
                          <li key={`miss-${idx}`}>
                            <code className="bg-gray-100 px-1 rounded">{m.text}</code>{" "}
                            <span className="text-xs text-gray-500">
                              (at {m.start}–{m.end})
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <div className="font-semibold text-sm mb-1">Extra (should not have matched):</div>
                    {gradeResult.extra.length === 0 ? (
                      <div className="text-xs text-green-700">None 🎉</div>
                    ) : (
                      <ul className="list-disc list-inside text-sm">
                        {gradeResult.extra.slice(0, 3).map((m, idx) => (
                          <li key={`extra-${idx}`}>
                            <code className="bg-gray-100 px-1 rounded">{m.text}</code>{" "}
                            <span className="text-xs text-gray-500">
                              (at {m.start}–{m.end})
                            </span>
                          </li>
                        ))}
                        {gradeResult.extra.length > 3 && (
                          <li className="text-xs text-gray-500 list-none">... and {gradeResult.extra.length - 3} more</li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ReadAndAsnwerRegex;
